import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box } from '@mui/material';
import FormInput from '../../../../components/forms/form-input';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/button';
import SectionWrapper from '../shared/section-wrapper';
import FormWrapper from '../shared/form-wrapper';
import { PasswordResetFormValues, passwordRules } from '../../utils';
import ValidationRules from '../shared/valiation-rules';
import { useDispatch } from 'react-redux';
import { useUpdatePasswordMutation } from '../../api/profileApi';
import { addNotification } from '../../../../features/notification/slices/notificationSlice';
import { NotificationType } from '../../../../features/notification/types';
import { i18n } from '@senderra/translations';

const checkSubRulesPasswordStrength = (password: string) => {
    const subRules = passwordRules.slice(2, passwordRules.length).map((rule) => rule.regex);
    const passedSubRulesCount = subRules.reduce((count, regex) => {
        return regex?.test(password) ? count + 1 : count;
    }, 0);
    return passedSubRulesCount >= 3;
};

const passwordSchema = yup.object().shape({
    newPassword: yup
        .string()
        .required(i18n.t('password-required'))
        .test('password-strength', i18n.t('password-validation-error'), (value = '') => {
            return passwordRules[0].regex?.test(value) && checkSubRulesPasswordStrength(value);
        }),
    confirmPassword: yup
        .string()
        .required(i18n.t('confirm-password-required'))
        .oneOf([yup.ref('newPassword')], i18n.t('confirm-password-validation-error')),
});

export const PasswordChange = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updatePassword] = useUpdatePasswordMutation();

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { isDirty, errors },
    } = useForm<PasswordResetFormValues>({
        resolver: yupResolver(passwordSchema),
        defaultValues: { newPassword: '', confirmPassword: '' },
    });

    const password = watch('newPassword');

    const onSubmit = async (data: PasswordResetFormValues) => {
        try {
            await updatePassword({ newPassword: data.confirmPassword }).unwrap();
            dispatch(addNotification({ message: t('password-change-success'), type: NotificationType.SUCCESS }));
            reset();
        } catch (error) {
            dispatch(addNotification({ message: t('password-change-failure'), type: NotificationType.ERROR }));
        }
    };

    return (
        <SectionWrapper>
            <Box component='form' onSubmit={handleSubmit(onSubmit)}>
                <FormWrapper>
                    <FormInput
                        name='newPassword'
                        label={t('new-password')}
                        register={register}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword?.message}
                        type='password'
                        testId='password'
                    />
                    <FormInput
                        name='confirmPassword'
                        label={t('re-enter-new-password')}
                        register={register}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        type='password'
                        testId='confirm-password'
                    />
                    {isDirty && (
                        <ValidationRules
                            watchField={password}
                            checkSubRulesPasswordStrength={checkSubRulesPasswordStrength}
                        />
                    )}
                </FormWrapper>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        width: '100%',
                        marginTop: '24px',
                        gap: '16px',
                    }}
                >
                    <Button label={t('cancel')} size='large' variant='outlined' onClick={() => reset()} />
                    <Button label={t('save')} size='large' variant='primary' type='submit' testId='submit' />
                </Box>
            </Box>
        </SectionWrapper>
    );
};

export default PasswordChange;
